import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "venturaz_cookie_consent_v1";

/**
 * Cookie consent banner (DPDPA 2023 + IT Rules aligned).
 * Persists user's choice locally; analytics scripts should read
 * `window.__venturaz_analytics_enabled` before firing.
 */
export default function CookieConsent() {
  const [decision, setDecision] = useState(null); // null | 'accepted' | 'essential'
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) { setVisible(true); return; }
      const parsed = JSON.parse(stored);
      setDecision(parsed.decision || null);
      window.__venturaz_analytics_enabled = parsed.decision === "accepted";
    } catch { setVisible(true); }
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        decision: choice, ts: new Date().toISOString(),
      }));
    } catch {}
    window.__venturaz_analytics_enabled = choice === "accepted";
    setDecision(choice);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[70] shadow-xl"
          data-testid="cookie-consent-banner">
      <div className="bg-ink-bg text-white border border-gold/40 p-4">
        <div className="flex items-start gap-3">
          <Cookie size={18} className="text-gold shrink-0 mt-0.5" />
          <div className="flex-1 text-xs leading-relaxed">
            <div className="overline text-gold mb-1">Your Privacy</div>
            <p className="text-white/85">
              VenturaZ uses essential cookies to keep you signed in and secure your session.
              With your consent we also use analytics cookies to improve the Private Market
              Intelligence experience. We do <b>not</b> sell your data or run behavioural ads.{" "}
              <Link to="/cms/cookie-policy" className="text-gold hover:underline">Read our Cookie Policy</Link>.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <button onClick={() => persist("accepted")}
                        className="bg-gold text-ink-bg text-[11px] font-mono uppercase tracking-widest px-3 py-2 hover:bg-gold/90"
                        data-testid="cookie-consent-accept">Accept all</button>
              <button onClick={() => persist("essential")}
                        className="border border-white/30 text-white text-[11px] font-mono uppercase tracking-widest px-3 py-2 hover:bg-white/10 hover:border-white/60"
                        data-testid="cookie-consent-essential">Essential only</button>
            </div>
          </div>
          <button onClick={() => persist("essential")}
                    className="text-white/60 hover:text-white shrink-0" title="Close (essential only)"
                    data-testid="cookie-consent-close">
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
